import { useState } from 'react';
import type { Asset } from '@cherry/shared';
import { shortenAddress } from '@cherry/shared';
import { useNftMetadata } from '../hooks/useNftMetadata';
import { useJettonMetadata } from '../hooks/useJettonMetadata';

const EXPLORER_BASE = 'https://tonviewer.com';

function formatTon(nano: string) {
  const n = Number(nano) / 1_000_000_000;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
}

function formatJettonAmount(raw: string, decimals: number) {
  const n = Number(raw) / 10 ** decimals;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
}

// ---------- Hero variant (large, for LotCard and LotDetailPage) ----------

type NftLikeAsset = Extract<Asset, { kind: 'NFT' | 'TG_GIFT' | 'TG_NUMBER' | 'TG_USERNAME' | 'DNS' }>;

function NftHero({ asset, large }: { asset: NftLikeAsset; large?: boolean }) {
  const { data: meta, isLoading } = useNftMetadata(asset.address);
  const imgSrc = large ? (meta?.imageLarge || meta?.image) : meta?.image;
  const name = meta?.name || shortenAddress(asset.address);
  const collection = meta?.collectionName;
  const model = meta?.attributes?.model;
  const isUnverified = !isLoading && !collection;

  return (
    <div className="relative w-full overflow-hidden bg-surface-alt">
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={name}
          className="w-full aspect-[4/3] object-cover"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <div className="w-full aspect-[4/3] flex items-center justify-center text-5xl bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-800/20">
          🖼
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 pt-8">
        <p className="text-white font-bold text-sm truncate drop-shadow-sm">{name}</p>
        {collection && (
          <div className="flex items-center gap-1 mt-0.5">
            <svg className="w-3 h-3 text-ton-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-white/80 text-xs truncate drop-shadow-sm">{collection}</span>
          </div>
        )}
        {model && (
          <span className="text-white/70 text-xs mt-0.5 block drop-shadow-sm">{model}</span>
        )}
        {isUnverified && asset.kind === 'NFT' && (
          <span className="text-white/50 text-xs italic mt-0.5 block">Unverified collection</span>
        )}
      </div>
    </div>
  );
}

function JettonHero({ asset }: { asset: Extract<Asset, { kind: 'JETTON' }> }) {
  const { data: meta } = useJettonMetadata(asset.master);
  const decimals = meta?.decimals ?? 9;
  const amount = formatJettonAmount(asset.amount, decimals);
  const symbol = meta?.symbol ?? shortenAddress(asset.master);
  const icon = meta?.imageUrl;

  return (
    <div className="w-full aspect-[4/3] flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/10">
      {icon ? (
        <img src={icon} alt="" className="w-16 h-16 rounded-full object-cover shadow-md mb-3" loading="lazy" />
      ) : (
        <div className="w-16 h-16 rounded-full bg-green-600 flex items-center justify-center text-2xl text-white shadow-md mb-3">🪙</div>
      )}
      <p className="text-xl font-bold text-text-primary">{amount}</p>
      <p className="text-sm text-text-secondary font-medium">{symbol}</p>
    </div>
  );
}

function TonHero({ asset }: { asset: Extract<Asset, { kind: 'TON' }> }) {
  const amount = formatTon(asset.amountNano);
  return (
    <div className="w-full aspect-[4/3] flex flex-col items-center justify-center bg-gradient-to-br from-ton-50 to-blue-50 dark:from-ton-500/10 dark:to-blue-900/10">
      <div className="w-16 h-16 rounded-full bg-ton-500 flex items-center justify-center text-2xl text-white shadow-md mb-3">💎</div>
      <p className="text-xl font-bold text-text-primary">{amount}</p>
      <p className="text-sm text-text-secondary font-medium">TON</p>
    </div>
  );
}

export function AssetShowcaseHero({ asset }: { asset: Asset }) {
  if (asset.kind === 'TON' && (!asset.amountNano || asset.amountNano === '0')) return null;

  if (asset.kind === 'NFT' || asset.kind === 'TG_GIFT' || asset.kind === 'TG_NUMBER' || asset.kind === 'TG_USERNAME' || asset.kind === 'DNS') {
    return <NftHero asset={asset} large />;
  }
  if (asset.kind === 'JETTON') return <JettonHero asset={asset} />;
  if (asset.kind === 'TON') return <TonHero asset={asset} />;
  return null;
}

// ---------- Thumbnail variant (small, for asset strip) ----------

function NftThumb({ asset }: { asset: NftLikeAsset }) {
  const { data: meta } = useNftMetadata(asset.address);
  const name = meta?.name || shortenAddress(asset.address);
  return (
    <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-surface-alt shrink-0" title={name}>
      {meta?.image ? (
        <img src={meta.image} alt={name} className="w-full h-full object-cover" loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-sm">🖼</div>
      )}
    </div>
  );
}

function JettonThumb({ asset }: { asset: Extract<Asset, { kind: 'JETTON' }> }) {
  const { data: meta } = useJettonMetadata(asset.master);
  const decimals = meta?.decimals ?? 9;
  const amount = formatJettonAmount(asset.amount, decimals);
  const symbol = meta?.symbol ?? '?';
  const icon = meta?.imageUrl;
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-alt border border-border shrink-0" title={`${amount} ${symbol}`}>
      {icon ? (
        <img src={icon} alt="" className="w-5 h-5 rounded-full object-cover" loading="lazy" />
      ) : (
        <span className="text-sm">🪙</span>
      )}
      <span className="text-xs font-bold text-text-primary whitespace-nowrap">{amount} {symbol}</span>
    </div>
  );
}

function TonThumb({ asset }: { asset: Extract<Asset, { kind: 'TON' }> }) {
  if (!asset.amountNano || asset.amountNano === '0') return null;
  const amount = formatTon(asset.amountNano);
  return (
    <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-surface-alt border border-border shrink-0">
      <span className="text-sm">💎</span>
      <span className="text-xs font-bold text-text-primary whitespace-nowrap">{amount} TON</span>
    </div>
  );
}

export function AssetShowcaseThumb({ asset }: { asset: Asset }) {
  if (asset.kind === 'TON') return <TonThumb asset={asset} />;
  if (asset.kind === 'JETTON') return <JettonThumb asset={asset} />;
  if (asset.kind === 'NFT' || asset.kind === 'TG_GIFT' || asset.kind === 'TG_NUMBER' || asset.kind === 'TG_USERNAME' || asset.kind === 'DNS') {
    return <NftThumb asset={asset} />;
  }
  return null;
}

// ---------- Detail card variant (for LotDetailPage gallery) ----------

const ATTRIBUTE_LABELS: Record<string, string> = {
  model: 'Model',
  backdrop: 'Backdrop',
  symbol: 'Symbol',
};

function GiftAttributeChips({ attributes }: { attributes: Record<string, string> }) {
  const entries = Object.entries(attributes).filter(
    ([k]) => k in ATTRIBUTE_LABELS,
  );
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {entries.map(([key, value]) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-surface-alt border border-border text-text-secondary"
        >
          <span className="text-text-muted">{ATTRIBUTE_LABELS[key]}:</span>
          <span className="font-medium text-text-primary">{value}</span>
        </span>
      ))}
    </div>
  );
}

function NftDetailCard({ asset }: { asset: NftLikeAsset }) {
  const { data: meta, isLoading } = useNftMetadata(asset.address);
  const [showMeta, setShowMeta] = useState(false);
  const name = meta?.name || shortenAddress(asset.address);
  const collection = meta?.collectionName;
  const isUnverified = !isLoading && !collection;
  const imgSrc = meta?.imageLarge || meta?.image;
  const attrs = meta?.attributes ?? {};

  const kindLabel = asset.kind === 'TG_GIFT' ? 'Telegram Gift' : asset.kind === 'TG_NUMBER' ? 'TG Number' : asset.kind === 'TG_USERNAME' ? 'Username' : asset.kind === 'DNS' ? '.ton DNS' : 'NFT';

  return (
    <div className="rounded-xl border border-border overflow-hidden bg-surface-raised">
      {imgSrc ? (
        <img src={imgSrc} alt={name} className="w-full aspect-square object-cover" loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      ) : (
        <div className="w-full aspect-square flex items-center justify-center text-6xl bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-800/20">
          🖼
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-bold text-sm truncate">{name}</h4>
          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 font-medium shrink-0">{kindLabel}</span>
        </div>
        {collection && (
          <div className="flex items-center gap-1 mt-1">
            <svg className="w-3.5 h-3.5 text-ton-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-xs text-text-secondary truncate">{collection}</span>
          </div>
        )}
        {isUnverified && asset.kind === 'NFT' && (
          <span className="text-xs text-text-muted italic mt-1 block">Unverified collection</span>
        )}

        <GiftAttributeChips attributes={attrs} />

        <button
          type="button"
          onClick={() => setShowMeta(!showMeta)}
          className="mt-2 text-xs text-ton-500 hover:text-ton-600 font-medium transition-colors"
        >
          {showMeta ? 'Hide details' : 'Details'}
        </button>

        {showMeta && (
          <div className="mt-2 pt-2 border-t border-border space-y-1.5 text-xs text-text-secondary">
            <div className="flex items-center justify-between gap-2">
              <span className="text-text-muted">Address</span>
              <code className="bg-surface-alt px-1.5 py-0.5 rounded text-xs">{shortenAddress(asset.address)}</code>
            </div>
            {meta?.collectionAddress && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-text-muted">Collection</span>
                <a href={`${EXPLORER_BASE}/${meta.collectionAddress}`} target="_blank" rel="noopener noreferrer"
                  className="text-ton-500 hover:text-ton-600 text-xs no-underline">{shortenAddress(meta.collectionAddress)} ↗</a>
              </div>
            )}
            <a href={`${EXPLORER_BASE}/${asset.address}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-ton-500 hover:text-ton-600 font-medium no-underline pt-1">
              View on Tonviewer ↗
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function JettonDetailCard({ asset }: { asset: Extract<Asset, { kind: 'JETTON' }> }) {
  const { data: meta } = useJettonMetadata(asset.master);
  const [showMeta, setShowMeta] = useState(false);
  const decimals = meta?.decimals ?? 9;
  const amount = formatJettonAmount(asset.amount, decimals);
  const symbol = meta?.symbol ?? shortenAddress(asset.master);
  const name = meta?.name ?? 'Unknown Token';
  const icon = meta?.imageUrl;

  return (
    <div className="rounded-xl border border-border overflow-hidden bg-surface-raised">
      <div className="p-4 flex items-center gap-4">
        {icon ? (
          <img src={icon} alt="" className="w-14 h-14 rounded-full object-cover shadow-sm shrink-0" loading="lazy" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center text-2xl text-white shadow-sm shrink-0">🪙</div>
        )}
        <div className="min-w-0">
          <p className="text-lg font-bold">{amount} <span className="text-text-secondary">{symbol}</span></p>
          <p className="text-sm text-text-muted truncate">{name}</p>
        </div>
      </div>
      <div className="px-4 pb-3">
        <button
          type="button"
          onClick={() => setShowMeta(!showMeta)}
          className="text-xs text-ton-500 hover:text-ton-600 font-medium transition-colors"
        >
          {showMeta ? 'Hide details' : 'Details'}
        </button>
        {showMeta && (
          <div className="mt-2 pt-2 border-t border-border space-y-1.5 text-xs text-text-secondary">
            <div className="flex items-center justify-between gap-2">
              <span className="text-text-muted">Contract</span>
              <code className="bg-surface-alt px-1.5 py-0.5 rounded text-xs">{shortenAddress(asset.master)}</code>
            </div>
            <a href={`${EXPLORER_BASE}/${asset.master}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-ton-500 hover:text-ton-600 font-medium no-underline pt-1">
              View on Tonviewer ↗
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function TonDetailCard({ asset }: { asset: Extract<Asset, { kind: 'TON' }> }) {
  if (!asset.amountNano || asset.amountNano === '0') return null;
  const amount = formatTon(asset.amountNano);
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-surface-raised">
      <div className="p-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-ton-500 flex items-center justify-center text-2xl text-white shadow-sm shrink-0">💎</div>
        <div>
          <p className="text-lg font-bold">{amount} <span className="text-text-secondary">TON</span></p>
          <p className="text-sm text-text-muted">Toncoin</p>
        </div>
      </div>
    </div>
  );
}

export function AssetDetailCard({ asset }: { asset: Asset }) {
  if (asset.kind === 'TON') return <TonDetailCard asset={asset} />;
  if (asset.kind === 'JETTON') return <JettonDetailCard asset={asset} />;
  if (asset.kind === 'NFT' || asset.kind === 'TG_GIFT' || asset.kind === 'TG_NUMBER' || asset.kind === 'TG_USERNAME' || asset.kind === 'DNS') {
    return <NftDetailCard asset={asset} />;
  }
  return null;
}
